import { Updater } from "@tanstack/react-table";
import { PlaylistDownloadDto, PlaylistItemStateDownloadDto, PlayListNodeDownloadDto, PlaylistStateDownloadDto } from "collaboration-service";
import SafeHTML from "components/SafeHTML/SafeHTML";
import { differenceInDays } from "date-fns";
import { Icon } from "imaginarity-react-ui";
import * as _ from "lodash";
import * as React from "react";
import { ApplicationState, useAppSelector } from "services/ApplicationState/ApplicationState";
import { addHoursToISODate, convertMinutesToDaysHoursMinutes, getTransitionMessage, shallowCompare, sortReferenceIdsInNodes } from "services/Helpers";
import ScrollHelper from "services/Helpers/ScrollHelper";
import { getTranslated, getTranslatedStandardDate } from "services/Helpers/TranslationHelpers";
import { useImgI18N } from "services/ImgI18N";
import { useAppTheme, useBelowBreakpointOrEqual } from "services/useAppTheme";
import { LocalStateEnum, NodeState, RefPostsInterface } from "views/Playlist/PlaylistDetail";
import PlaylistDetailNodeItem from "views/Playlist/PlaylistDetailNodeItem";
import { PlaylistDetailSC as T } from 'views/Playlist/PlaylistDetailSC';
import { PlaylistTheme } from "views/Playlist/PlaylistMain";


interface PlaylistDetailNodesToShowProps {
    nodesToShow: PlayListNodeDownloadDto[];
    playlistState?: PlaylistStateDownloadDto;
    nodeContentVisible: boolean[];
    setNodeContentVisible: (visible: Updater<boolean[]>) => void;
    scrollHelper: ScrollHelper;
    refPosts: RefPostsInterface;
    reloadPlaylistState: (toBeDone?: string) => Promise<void>;
    palmsLinks: Record<string, string>;
}

const mapper = (s: ApplicationState) => ({
    contentLanguage: s.contentLanguage
})

const PlaylistDetailNodesToShow = (p: PlaylistDetailNodesToShowProps) => {
    const { contentLanguage } = useAppSelector(mapper, shallowCompare);
    const { nodesToShow, playlistState, nodeContentVisible, scrollHelper, refPosts, reloadPlaylistState, palmsLinks, setNodeContentVisible } = p;
    const { t } = useImgI18N("playlist");
    const playlistTheme = useAppTheme<PlaylistTheme>("playlistTheme");
    const flushText = playlistTheme.flushText;

    const isMobile = useBelowBreakpointOrEqual("largeMobile");

    const {
        curNode,
        playlistIsFinished,
        prevNodes,
        hasNextNodes,
        playlistStateIsOptional
    } = React.useMemo(() => {
        const curNode = playlistState?.curNodes[0];
        const playlistIsFinished = playlistState?.state === "Finished";
        const prevNodes = sortReferenceIdsInNodes(playlistState?.prevNodes ?? []);
        const nextNodes = sortReferenceIdsInNodes(playlistState?.nextNodes ?? []);
        const hasNextNodes = nextNodes.length > 0;
        const playlistStateIsOptional = playlistState?.optional;
        return {
            curNode,
            playlistIsFinished,
            prevNodes,
            hasNextNodes,
            playlistStateIsOptional
        }
    }, [playlistState])


    const toggleNodeContent = React.useCallback((index: number) => () => {
        const updatedVisibleState = [...nodeContentVisible];
        updatedVisibleState[index] = !updatedVisibleState[index];
        setNodeContentVisible(updatedVisibleState);
    }, [nodeContentVisible, setNodeContentVisible]);

    const getCurrentLearningStatus = React.useMemo(() => (): LocalStateEnum => {
        if (playlistState?.optional)
            return "Optional";
        if (!curNode)
            return playlistState?.state === "Finished" ? "Finished" : "On Track";
        switch (curNode?.state) {
            case "Finished":
                return "Finished";
            case "Overdue":
                return "Overdue";
            default:
                return "On Track";
        }
    }, [playlistState, curNode]);

    const getNodeState = React.useMemo(() => (n: PlayListNodeDownloadDto, dif: number): NodeState => {
        let ret = {
            state: n?.state === "Finished" ? "Finished" : getCurrentLearningStatus(),
            finished: n?.completedCount ?? 0,
        } as NodeState;
        return ret;
    }, [getCurrentLearningStatus]);

    return (
        <React.Fragment>
            {_.map(nodesToShow, (n, index) => {
                const estDuration = n.durationInMinutes ? convertMinutesToDaysHoursMinutes(n.durationInMinutes, t, true) : undefined;
                const isAbsDueDate = n.dueDateRelativeTo && n.dueDateRelativeTo === "Absolute";
                const isRelDueDate = n.dueDateRelativeTo && n.dueDateRelativeTo !== "Absolute";
                const hasAbsDueDate = isAbsDueDate && n.absDueDate;
                const hasRelDueDate = isRelDueDate && n.dueDateRelativeInHours && n.dueDateRelativeInHours > 0;
                let dueToDate = hasAbsDueDate ? n.absDueDate : (hasRelDueDate && playlistState?.assignmentDate && n.dueDateRelativeInHours) ? addHoursToISODate(playlistState.assignmentDate, n.dueDateRelativeInHours) : new Date();
                const dif = differenceInDays(new Date(dueToDate as Date), new Date());
                // const dueToDateText = dif < 14 ? formatDistanceToNow(new Date(dueToDate as Date), { addSuffix: true, locale: getDateFnsLng }) : formatRelative(new Date(dueToDate as Date), new Date(), { locale: getDateFnsLng });
                const nodeState = getNodeState(n, dif);
                const stateOfNode = nodeState.state;
                const finished = n.completedCount ?? 0;
                const countNode = n.relevantItemCount ?? 0;
                const transition = _.first(n.children)?.transitionFunction;
                const isOptionalHint = transition === "FallThru" ? t("optional") : undefined;
                const hasAbsoluteDueDate = n.dueDateRelativeTo === "Absolute" && n.absDueDate !== undefined;
                const hasRelativeDueDate = (n.dueDateRelativeTo !== undefined || n.dueDateRelativeTo !== "Absolute") && n.dueDateRelativeInHours !== undefined;
                const dueToType = hasAbsoluteDueDate ? "absolute" : hasRelativeDueDate ? "relative" : undefined;
                // const dueToDateText = dif === 0 ? (isOptionalHint ?? "") : (dif < 14 ? formatRelative(new Date(dueToDate as Date), new Date(), { locale: getDateFnsLng }) : formatRelative(new Date(dueToDate as Date), new Date(), { locale: getDateFnsLng }));
                const dueToDateText = dif === 0 ? (isOptionalHint ?? "") : (dueToDate && getTranslatedStandardDate(dueToDate, "PP"));


                const chechIfIsToday = (dateValue: Date): boolean => {
                    const today = new Date();

                    return dateValue.getDate() === today.getDate() &&
                        dateValue.getMonth() === today.getMonth() &&
                        dateValue.getFullYear() === today.getFullYear();
                };

                const toCheck = new Date(dueToDate as Date);
                const isToday = chechIfIsToday(toCheck);

                const timeRunsOut = isToday || !playlistIsFinished;
                const isInPrevNode = _.filter(prevNodes, p => p.id === n.id).length > 0;




                const finishedPercent = n.referenceIds ? (finished / countNode * 100) + "%" : "0%";
                const nodeOverdue = !timeRunsOut && stateOfNode === "Overdue";
                const nodeOnTrack = !timeRunsOut && stateOfNode === "On Track";
                const nodeFinished = !timeRunsOut && (stateOfNode === "Finished" || playlistIsFinished);
                const infoText = transition && getTransitionMessage(t, transition, hasNextNodes, playlistIsFinished, nodeFinished, _.first(n.children)?.referenceValue, n.referenceIds?.length, _.first(n.children)?.referenceContentId);

                return (
                    <React.Fragment key={n.id}>
                        <T.NodeBar flushText={flushText} isMobile={isMobile} onClick={toggleNodeContent(index)} ref={scrollHelper.getRef(n.id ?? '')}>
                            <T.NodeBarIcon isVisible={nodeContentVisible[index]}>
                                <Icon name={nodeContentVisible[index] ? "chevron up" : "chevron down"} size="20px" style={{ float: "left" }} marginTop={0} />
                            </T.NodeBarIcon>
                            <T.NodeBarTitle>
                                <SafeHTML html={getTranslated(n.headlines, contentLanguage)?.text} allowedTags={[]} allowedAttributes={{}} />
                            </T.NodeBarTitle>
                            {isMobile ?
                                <>
                                    {isOptionalHint ?
                                        <T.NodeBarInfo flushText={flushText}>
                                            {isOptionalHint}
                                        </T.NodeBarInfo>
                                        :
                                        <T.NodeBarInfo flushText={flushText} color={isInPrevNode ? "@accentGreen" : timeRunsOut ? "@warning" : stateOfNode === "Overdue" ? "@accentRed" : stateOfNode === "Finished" ? "@accentGreen" : undefined} style={{ textAlign: flushText ? "left" : "right" }}>
                                            {timeRunsOut && !playlistStateIsOptional && <> <Icon name="exclamation triangle" style={{ float: "left", marginRight: 5 }} />{t("warning")}</>}
                                            {nodeFinished && !playlistStateIsOptional && <> <Icon name="check circle" style={{ float: "left", marginRight: 5 }} />{t("done")}</>}
                                            {nodeOnTrack && !playlistStateIsOptional && <> <Icon name={isInPrevNode ? "check circle" : "clock"} style={{ float: "left", marginRight: 5 }} />{isInPrevNode ? t("done") : t("on track")}</>}
                                            {nodeOverdue && !playlistStateIsOptional && <> <Icon name="exclamation circle" style={{ float: "left", marginRight: 5 }} />{t("overdue")}</>}
                                        </T.NodeBarInfo>
                                    }
                                </>
                                :
                                <>
                                    {!isOptionalHint &&
                                        <T.NodeBarInfo
                                            flushText={flushText}
                                            color={isInPrevNode ? "@accentGreen" : timeRunsOut ? "@warning" : stateOfNode === "Overdue" ? "@accentRed" : stateOfNode === "Finished" ? "@accentGreen" : undefined}
                                            style={{ textAlign: flushText ? "left" : "right", width: 200 }}
                                        >
                                            {nodeFinished ?
                                                <>
                                                    <Icon name="check circle" style={{ float: flushText ? "left" : "right", marginRight: flushText ? 5 : 0, marginLeft: flushText ? 0 : 5 }} />
                                                    {t("done")}
                                                </>
                                                :
                                                <>
                                                    {timeRunsOut && !isInPrevNode && !playlistStateIsOptional &&
                                                        <>
                                                            <Icon name="exclamation triangle" style={{ float: flushText ? "left" : "right", marginRight: flushText ? 5 : 0, marginLeft: flushText ? 0 : 5 }} />
                                                            {t("warning")}
                                                        </>
                                                    }
                                                    {nodeOnTrack && !isInPrevNode && !playlistStateIsOptional &&
                                                        <>
                                                            <Icon name={"clock"} style={{ float: flushText ? "left" : "right", marginRight: flushText ? 5 : 0, marginLeft: flushText ? 0 : 5 }} />
                                                            {t("on track")}
                                                        </>
                                                    }
                                                    {nodeOverdue && !isInPrevNode && !playlistStateIsOptional &&
                                                        <>
                                                            <Icon name="exclamation circle" style={{ float: flushText ? "left" : "right", marginRight: flushText ? 5 : 0, marginLeft: flushText ? 0 : 5 }} />
                                                            {t("overdue")}
                                                        </>
                                                    }

                                                    {isInPrevNode &&
                                                        <>
                                                            <Icon name="check circle" style={{ float: flushText ? "left" : "right", marginRight: flushText ? 5 : 0, marginLeft: flushText ? 0 : 5 }} />
                                                            {t("done")}
                                                        </>
                                                    }
                                                </>
                                            }
                                        </T.NodeBarInfo>
                                    }
                                    {playlistStateIsOptional ?
                                        <T.NodeBarInfo flushText={flushText} style={{ width: 100 }} />
                                        :
                                        <T.NodeBarInfo flushText={flushText} style={{ width: 100 }}>
                                            {estDuration}
                                        </T.NodeBarInfo>
                                    }
                                    {playlistStateIsOptional ?
                                        <T.NodeBarInfo flushText={flushText} style={{ width: 150 }}>
                                            {estDuration}
                                        </T.NodeBarInfo>
                                        :
                                        // isOptionalHint ?
                                        //     <T.NodeBarInfo flushText={flushText} style={{ width: 150 }}>
                                        //         <T.DueDateOptionalHint>{isOptionalHint}</T.DueDateOptionalHint>
                                        //         {dueToDateText}
                                        //     </T.NodeBarInfo>
                                        //     :
                                        <T.NodeBarInfo flushText={flushText} style={{ width: 150 }}>
                                            {dueToDateText}
                                        </T.NodeBarInfo>
                                    }

                                </>
                            }
                        </T.NodeBar>
                        {(nodeContentVisible[index]) ?
                            <T.NodeContentContainer >
                                <T.NodeContentContainerInfoLine>
                                    <div>
                                        {t("{{done}} / {{of}} completed", { done: finished, of: countNode })}
                                    </div>


                                    {infoText && !playlistIsFinished && !nodeFinished &&
                                        <T.NodeContentContainerHint >
                                            <Icon name="info" style={{ float: "left", marginRight: 5 }} marginTop={5} />
                                            {infoText}
                                        </T.NodeContentContainerHint>
                                    }

                                    <div style={{ position: "absolute", bottom: -1, left: 0, width: finishedPercent, height: 1, background: "#313639" }} />
                                </T.NodeContentContainerInfoLine>

                                {n.descriptions && n.descriptions.length > 0 &&
                                    <T.NodeContentContainerInfoLine style={{ lineHeight: "unset" }}>
                                        <SafeHTML html={getTranslated(n.descriptions, contentLanguage)?.text} />
                                    </T.NodeContentContainerInfoLine>
                                }

                                {Object.keys(refPosts).length > 0 && _.map(n.referenceIds, (refId, idx) => {
                                    const p = refPosts[refId];
                                    if (p === undefined)
                                        return false;
                                    const key = p.id;
                                    const isOptional = transition === "FallThru" || stateOfNode === "Finished";
                                    const state = _.find(playlistState?.itemStates, st => st.referenceId === refId)
                                        ?? _.find(playlistState?.prevItemStates, st => st.referenceId === refId);

                                    const firstChild = _.first(n.children);
                                    const transIsRef = firstChild?.transitionFunction === "ReferenceDone" || firstChild?.transitionFunction === "ReferenceDoneWithValue";
                                    const transIsRefVal = (firstChild && firstChild.referenceValue);
                                    const nodeReferences: string[] | undefined = (firstChild && transIsRef && transIsRefVal) ? firstChild.referenceContentIds : undefined;
                                    const nodeReference = _.find(nodeReferences, nr => nr === state?.referenceId) ?? undefined;
                                    const content = _.find((playlistState?.playlist as PlaylistDownloadDto)?.contents, c => c.referenceId === refId);

                                    return (<React.Fragment key={key}>
                                        <PlaylistDetailNodeItem
                                            item={p}
                                            isMobile={isMobile}
                                            state={state ?? { state: 'NotAvailable' } as PlaylistItemStateDownloadDto}
                                            playlistId={playlistState?.playlist?.id}
                                            orderNumber={idx + 1}
                                            playlistStateId={playlistState?.id ?? ''}
                                            isOptional={isOptional || nodeFinished || playlistStateIsOptional}
                                            dueToType={dueToType}
                                            assignmentDate={playlistState?.assignmentDate}
                                            absoluteDueDate={n.absDueDate}
                                            dueDateRelativeInHours={n.dueDateRelativeInHours}
                                            nodeReference={nodeReference}
                                            transition={firstChild?.transitionFunction}
                                            warning={timeRunsOut}
                                            accentColor=''
                                            nodeIsDone={isInPrevNode}
                                            loadPlaylistStates={reloadPlaylistState}
                                            playlistContent={content}
                                            palmsLinks={palmsLinks}
                                            node={n}

                                        />
                                    </React.Fragment>
                                    );
                                })}
                            </T.NodeContentContainer>
                            :
                            <div style={{ height: 24 }} />
                        }
                    </React.Fragment>
                );
            })}
        </React.Fragment>
    )
}

export default PlaylistDetailNodesToShow;